import { ChartOfAccount, COAFormData, COAFilters } from '../types/coa.types'

// Mock data for demonstration
const mockAccounts: ChartOfAccount[] = [
  {
    id: '1',
    accountCode: '1000',
    accountName: 'Assets',
    accountType: 'Asset',
    subType: 'Current Asset',
    openingDate: '2024-01-01',
    isInactive: false,
    balance: 150000,
    hasTransactions: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  {
    id: '2',
    accountCode: '1100',
    accountName: 'Cash and Bank',
    parentId: '1',
    accountType: 'Asset',
    subType: 'Bank',
    openingDate: '2024-01-01',
    isInactive: false,
    bankNameId: 'bank1',
    bankName: 'Premier Bank',
    bankBranchId: 'branch1',
    bankBranch: 'Main Branch',
    bankAccountNo: '1234567890',
    balance: 75000,
    hasTransactions: true,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  {
    id: '3',
    accountCode: '2000',
    accountName: 'Liabilities',
    accountType: 'Liability',
    subType: 'Current Liability',
    openingDate: '2024-01-01',
    isInactive: false,
    balance: 50000,
    hasTransactions: false,
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
]

class COAService {
  private accounts: ChartOfAccount[] = mockAccounts

  async getAccounts(filters?: COAFilters): Promise<ChartOfAccount[]> {
    let filtered = [...this.accounts]

    if (filters) {
      if (filters.accountCode) {
        filtered = filtered.filter((a) =>
          a.accountCode
            .toLowerCase()
            .includes(filters.accountCode!.toLowerCase()),
        )
      }
      if (filters.accountName) {
        filtered = filtered.filter((a) =>
          a.accountName
            .toLowerCase()
            .includes(filters.accountName!.toLowerCase()),
        )
      }
      if (filters.accountType) {
        filtered = filtered.filter((a) => a.accountType === filters.accountType)
      }
      if (filters.subType) {
        filtered = filtered.filter((a) => a.subType === filters.subType)
      }
      if (filters.parentId) {
        filtered = filtered.filter((a) => a.parentId === filters.parentId)
      }
    }

    return filtered
  }

  async getAccountTree(): Promise<ChartOfAccount[]> {
    const buildTree = (parentId?: string): ChartOfAccount[] => {
      return this.accounts
        .filter((a) => a.parentId === parentId)
        .map((account) => ({
          ...account,
          children: buildTree(account.id),
        }))
    }

    return buildTree()
  }

  async getAccountById(id: string): Promise<ChartOfAccount | null> {
    return this.accounts.find((a) => a.id === id) || null
  }

  async createAccount(data: COAFormData): Promise<ChartOfAccount> {
    const newAccount: ChartOfAccount = {
      id: Date.now().toString(),
      ...data,
      accountType: data.accountType as any,
      subType: data.subType as any,
      balance: 0,
      hasTransactions: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    this.accounts.push(newAccount)
    return newAccount
  }

  async updateAccount(
    id: string,
    data: Partial<COAFormData>,
  ): Promise<ChartOfAccount> {
    const index = this.accounts.findIndex((a) => a.id === id)
    if (index === -1) throw new Error('Account not found')

    const account = this.accounts[index]
    if (account.hasTransactions) {
      // Only allow safe edits
      const safeFields = [
        'accountName',
        'accountNameSomali',
        'description',
        'additionalNote',
      ]
      const unsafeEdit = Object.keys(data).some(
        (key) => !safeFields.includes(key),
      )
      if (unsafeEdit) {
        throw new Error('Cannot modify account with existing transactions')
      }
    }

    this.accounts[index] = {
      ...account,
      ...data,
      updatedAt: new Date().toISOString(),
    }

    return this.accounts[index]
  }

  async deleteAccount(id: string): Promise<void> {
    const account = this.accounts.find((a) => a.id === id)
    if (!account) throw new Error('Account not found')
    if (account.hasTransactions) {
      throw new Error('Cannot delete account with existing transactions')
    }

    this.accounts = this.accounts.filter((a) => a.id !== id)
  }

  async validateAccountCode(
    code: string,
    excludeId?: string,
  ): Promise<boolean> {
    return !this.accounts.some(
      (a) => a.accountCode === code && a.id !== excludeId,
    )
  }

  async validateAccountName(
    name: string,
    excludeId?: string,
  ): Promise<boolean> {
    return !this.accounts.some(
      (a) => a.accountName === name && a.id !== excludeId,
    )
  }
}

export const coaService = new COAService()
